import { useMemo, useState } from "react";
import "./ProductionDashboard.scss";
import DepartmentQualityChart from "./DepartmentQualityChart";
import DepartmentProductionSummary from "./DepartmentProductionSummary";
import ProductionMixChart from "./ProductionMixChart";
import ProductionTrendByDepartmentChart from "./ProductionTrendByDepartmentChart";
import TopRejectionDepartments from "./TopRejectionDepartments";

import cardImg from "../../../../images/Images/card.png";
import okImg from "../../../../images/Images/ok.png";
import rejectImg from "../../../../images/Images/reject.png";
import reworkImg from "../../../../images/Images/rework.png";
import departmentImg from "../../../../images/Images/department.png";

//Redux
import { useSelector } from "react-redux";
import Loader from "../../../../../common/Loader";

// const formatNumber = (value) => {
//   if (value === null || value === undefined || value === "") return "0";

//   return new Intl.NumberFormat("en-IN", {
//     maximumFractionDigits: 2,
//   }).format(Number(value) || 0);
// };

const round = (value, decimals = 2) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 0;

  const factor = Math.pow(10, decimals);
  return Math.round(numberValue * factor) / factor;
};

const normalizeDepartmentName = (name = "") => {
  // Handles duplicate display names like "DEPT014 - BENDING"
  return String(name)
    .replace(/^DEPT\d+\s*-\s*/i, "")
    .trim();
};

const sortByDeptCode = (a, b) => {
  const deptCodeA = String(a?.deptCode || "");
  const deptCodeB = String(b?.deptCode || "");

  return deptCodeA.localeCompare(deptCodeB, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const FINAL_PRODUCTION_DEPARTMENT = "DEPT018";

function ProductionDashboard({
  data = null,
  loading = false,
  filters,
  dataToday = null,
}) {
  const appConfig = useSelector(
    (state) => state?.common?.data?.dashboardControl,
  ); // User Details from Store

  // eslint-disable-next-line no-unused-vars
  const [viewMode, setViewMode] = useState("all"); // "all" or "top10"
  const [dataTodayInfo, setDataTodayInfo] = useState(null);

  // Production Trend chart view
  const [productionTrendChartType, setProductionTrendChartType] =
    useState("area"); // "column" or "area"

  // Quality chart view
  const [qualityChartType, setQualityChartType] = useState("column");

  const departmentData = useMemo(() => {
    setDataTodayInfo(dataToday);
    const list = data?.departmentWiseProduction || [];
    const map = new Map();

    list.forEach((item) => {
      const key = item?.deptCode || item?.deptName;

      if (!map.has(key)) {
        map.set(key, {
          deptCode: item?.deptCode || "",
          deptName: normalizeDepartmentName(item?.deptName || "Unknown"),
          totalQty: 0,
          okQty: 0,
          rejectQty: 0,
          reworkQty: 0,
          totalRecords: 0,
          jobcardCount: 0,
          machineCount: 0,
          createdUserCount: 0,
        });
      }

      const row = map.get(key);

      row.totalQty += Number(item?.totalQty || 0);
      row.okQty += Number(item?.okQty || 0);
      row.rejectQty += Number(item?.rejectQty || 0);
      row.reworkQty += Number(item?.reworkQty || 0);
      row.totalRecords += Number(item?.totalRecords || 0);
      row.jobcardCount += Number(item?.jobcardCount || 0);
      row.machineCount += Number(item?.machineCount || 0);
      row.createdUserCount += Number(item?.createdUserCount || 0);
    });

    return Array.from(map.values())
      .map((item) => ({
        ...item,

        okPercentage:
          item.totalQty > 0 ? round((item.okQty / item.totalQty) * 100, 2) : 0,

        rejectionPercentage:
          item.totalQty > 0
            ? round((item.rejectQty / item.totalQty) * 100, 2)
            : 0,
      }))
      .sort(sortByDeptCode);
  }, [data, dataToday]);

  const filteredDepartments = useMemo(() => {
    if (viewMode === "all") return departmentData;
    return departmentData.slice(0, 10);
  }, [departmentData, viewMode]);

  const summary = useMemo(() => {
    const finalDepartment = departmentData.find(
      (item) =>
        String(item.deptCode || "").trim().toUpperCase() ===
        FINAL_PRODUCTION_DEPARTMENT,
    );
    const totalOk = Number(finalDepartment?.okQty || 0);

    const totalReject = departmentData.reduce(
      (sum, item) => sum + Number(item.rejectQty || 0),
      0,
    );

    const totalRework = departmentData.reduce(
      (sum, item) => sum + Number(item.reworkQty || 0),
      0,
    );
    const totalProduced = totalOk + totalReject + totalRework;

    const totalMachines = departmentData.reduce(
      (sum, item) => sum + Number(item.machineCount || 0),
      0,
    );
    const totalCreatedUserCount = departmentData.reduce(
      (sum, item) => sum + Number(item.createdUserCount || 0),
      0,
    );

    return {
      totalProduced,
      totalOk,
      totalReject,
      totalRework,
      totalDepartments: departmentData.length,
      totalMachines,
      totalCreatedUserCount,
      okPercentage:
        totalProduced > 0 ? round((totalOk / totalProduced) * 100, 2) : 0,
      rejectionPercentage:
        totalProduced > 0 ? round((totalReject / totalProduced) * 100, 2) : 0,
    };
  }, [departmentData]);

  const categoryLabels = useMemo(() => {
    return filteredDepartments.map((item) => item.deptName);
  }, [filteredDepartments]);

  const chartKey = useMemo(() => {
    return filteredDepartments
      .map((item) => `${item.deptCode}-${item.totalQty}-${item.rejectQty}`)
      .join("|");
  }, [filteredDepartments]);

  const productionTrendSeries = useMemo(() => {
    return [
      {
        name: "Total Produced",
        data: filteredDepartments.map((item) => Number(item.totalQty || 0)),
      },
      {
        name: "OK Qty",
        data: filteredDepartments.map((item) => Number(item.okQty || 0)),
      },
      {
        name: "Reject Qty",
        data: filteredDepartments.map((item) => Number(item.rejectQty || 0)),
      },
      {
        name: "Rework Qty",
        data: filteredDepartments.map((item) => Number(item.reworkQty || 0)),
      },
    ];
  }, [filteredDepartments]);

  const qualitySeries = useMemo(() => {
    return [
      {
        name: "OK %",
        data: filteredDepartments.map((item) => Number(item.okPercentage || 0)),
      },
      {
        name: "Reject %",
        data: filteredDepartments.map((item) =>
          Number(item.rejectionPercentage || 0),
        ),
      },
    ];
  }, [filteredDepartments]);

  const donutSeries = useMemo(() => {
    return [
      Number(summary.totalOk || 0),
      Number(summary.totalReject || 0),
      Number(summary.totalRework || 0),
    ];
  }, [summary.totalOk, summary.totalReject, summary.totalRework]);

  const productionTrendChartData = useMemo(() => {
    return {
      sortedDeptNames: categoryLabels,
      chartData: productionTrendSeries,
    };
  }, [categoryLabels, productionTrendSeries]);

  const qualityChartData = useMemo(() => {
    return {
      sortedDeptNames: categoryLabels,
      chartData: qualitySeries,
    };
  }, [categoryLabels, qualitySeries]);

  const topRejectDepartments = useMemo(() => {
    return [...departmentData]
      .sort((a, b) => b.rejectionPercentage - a.rejectionPercentage)
      .slice(0, 6);
  }, [departmentData]);

  const safeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const formatNumber = (value, maximumFractionDigits = 2) =>
    new Intl.NumberFormat("en-IN", {
      maximumFractionDigits,
    }).format(safeNumber(value));

  // All hooks are completed above.
  // Conditional return should come only after all hooks.
  
  if (loading || data === null) {
    return (
      <section className="production-dashboard-section">
        <div className="production-dashboard-card loading-card">
          Loading production dashboard...
          <Loader isLoading={true} className={"text-center"} />
        </div>
      </section>
    );
  }

  if (!departmentData.length) {
    return (
      <section className="production-dashboard-section">
        <div className="production-dashboard-card empty-card">
          <h3>No production data found</h3>
          <p>Please change the date filter and refresh the dashboard.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="production-dashboard-section">
      {/* <div className="production-dashboard-header">
        <div>
          <h2>Department-wise Production Trend</h2>
          <p>
            Track produced quantity, OK quantity, rejection, rework and quality
            percentage across all departments.
          </p>
        </div>

        <div className="production-actions">
          <button
            type="button"
            className={viewMode === "top10" ? "active" : ""}
            onClick={() => setViewMode("top10")}
          >
            Top 10
          </button>
          <button
            type="button"
            className={viewMode === "all" ? "active" : ""}
            onClick={() => setViewMode("all")}
          >
            All
          </button>
        </div>
      </div> */}

      <div className="production-kpi-grid animated-kpi-grid">
        <div
          className="production-kpi-card purple animated-kpi-card"
          style={{ "--delay": "0ms" }}
        >
          <span>Departments / Machines</span>
          <strong>
            <img
              src={departmentImg}
              alt="KPI Icon"
              className="dashboard-kpi-image"
            />
            {formatNumber(summary.totalDepartments)} /{" "}
            {formatNumber(summary.totalMachines)}
          </strong>
          <small> Count</small>
        </div>

        <div
          className="production-kpi-card primary animated-kpi-card"
          style={{ "--delay": "120ms" }}
        >
          <span>Total Jobcards</span>
          <strong>
            <img src={cardImg} alt="KPI Icon" className="dashboard-kpi-image" />
            {formatNumber(dataTodayInfo?.info?.jobcardCount)}
          </strong>
          <small>Batches</small>
        </div>

        <div
          className="production-kpi-card success animated-kpi-card"
          style={{ "--delay": "240ms" }}
        >
          <span>OK Quantity</span>
          <strong>
            <img src={okImg} alt="KPI Icon" className="dashboard-kpi-image" />
            {formatNumber(summary.totalOk)}
          </strong>
          <small>{summary.okPercentage}% Quality</small>
        </div>

        <div
          className="production-kpi-card danger animated-kpi-card"
          style={{ "--delay": "360ms" }}
        >
          <span>Reject Quantity</span>
          <strong>
            <img
              src={rejectImg}
              alt="KPI Icon"
              className="dashboard-kpi-image"
            />
            {formatNumber(summary.totalReject)}
          </strong>
          <small>{summary.rejectionPercentage}% Rejection</small>
        </div>

        <div
          className="production-kpi-card warning animated-kpi-card"
          style={{ "--delay": "480ms" }}
        >
          <span>Rework Quantity</span>
          <strong>
            <img
              src={reworkImg}
              alt="KPI Icon"
              className="dashboard-kpi-image"
            />
            {formatNumber(summary.totalRework)}
          </strong>
          <small>Units</small>
        </div>
      </div>

      <div className="production-chart-grid">
        <div className="production-dashboard-card large">
          <div className="card-title-row">
            <div>
              <h3>Production Trend by Department</h3>
              <p>Total, OK, rejection and rework quantity</p>
            </div>

            <div className="chart-view-switch">
              <button
                type="button"
                className={
                  productionTrendChartType === "column" ? "active" : ""
                }
                onClick={() => setProductionTrendChartType("column")}
                aria-label="Show column chart"
              >
                <span className="chart-switch-icon column-icon">
                  <i></i>
                  <i></i>
                  <i></i>
                </span>

                <span>Column</span>
              </button>

              <button
                type="button"
                className={productionTrendChartType === "area" ? "active" : ""}
                onClick={() => setProductionTrendChartType("area")}
                aria-label="Show area chart"
              >
                <span className="chart-switch-icon area-icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 18L8 11L12 14L17 7L21 10V20H3V18Z"
                      fill="currentColor"
                      opacity="0.25"
                    />

                    <path
                      d="M3 18L8 11L12 14L17 7L21 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>

                <span>Area</span>
              </button>
            </div>
          </div>

          <ProductionTrendByDepartmentChart
            chartdata={productionTrendChartData}
            chartKey={`${chartKey}-${productionTrendChartType}`}
            chartType={productionTrendChartType}
          />
        </div>

        <TopRejectionDepartments departments={topRejectDepartments} />
      </div>

      {(appConfig?.QualityPercentagebyDepartment ||
        appConfig?.ProductionMix) && (
        <div className="production-chart-grid">
          {appConfig?.QualityPercentagebyDepartment && (
            <div className="production-dashboard-card large">
              <div className="card-title-row">
                <div>
                  <h3>Quality Percentage by Department</h3>
                  <p>OK percentage compared with rejection percentage</p>
                </div>

                <div className="chart-view-switch">
                  <button
                    type="button"
                    className={qualityChartType === "column" ? "active" : ""}
                    onClick={() => setQualityChartType("column")}
                    aria-label="Show column chart"
                  >
                    <span className="chart-switch-icon column-icon">
                      <i></i>
                      <i></i>
                      <i></i>
                    </span>

                    <span>Column</span>
                  </button>

                  <button
                    type="button"
                    className={qualityChartType === "area" ? "active" : ""}
                    onClick={() => setQualityChartType("area")}
                    aria-label="Show area chart"
                  >
                    <span className="chart-switch-icon area-icon">
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 18L8 11L12 14L17 7L21 10V20H3V18Z"
                          fill="currentColor"
                          opacity="0.25"
                        />

                        <path
                          d="M3 18L8 11L12 14L17 7L21 10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>

                    <span>Area</span>
                  </button>
                </div>
              </div>

              <DepartmentQualityChart
                chartdata={qualityChartData}
                chartKey={`${chartKey}-${qualityChartType}`}
                chartType={qualityChartType}
              />
            </div>
          )}

          {appConfig?.ProductionMix && (
            <div className="production-dashboard-card">
              <div className="card-title-row">
                <div>
                  <h3>Production Mix</h3>
                  <p>OK vs Reject vs Rework</p>
                </div>
              </div>

              <ProductionMixChart
                series={donutSeries}
                totalProduced={summary.totalProduced}
                chartKey={chartKey}
              />
            </div>
          )}
        </div>
      )}

      <DepartmentProductionSummary departments={departmentData} filters={filters} />
    </section>
  );
}

export default ProductionDashboard;
